/**
 * Browser media device utilities (camera, microphone, speaker).
 * Uses the MediaDevices API to enumerate and resolve current devices.
 */

import { devLog } from './devLog';

export const DEVICE_LS_KEYS = {
  camera: 'settings.selectedCamera',
  microphone: 'settings.selectedMicrophone',
  audioOutput: 'settings.selectedAudioOutput',
} as const;

const SANGOMA_SETTINGS_KEY = 'sangoma-meet-settings';

export interface CurrentBrowserDevices {
  selectedCamera: string;
  selectedMicrophone: string;
  selectedAudioOutput: string;
}

const FALLBACK: CurrentBrowserDevices = {
  selectedCamera: '',
  selectedMicrophone: '',
  selectedAudioOutput: '',
};

async function ensureMediaPermission(): Promise<void> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
    stream.getTracks().forEach((t) => {
      t.stop();
    });
  } catch {
    // Permission denied or not available; enumerateDevices may still return devices with empty labels
  }
}

export async function getCurrentBrowserDevices(): Promise<CurrentBrowserDevices> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
    return { ...FALLBACK };
  }

  try {
    await ensureMediaPermission();
    const devices = await navigator.mediaDevices.enumerateDevices();

    const videoInputs = devices.filter((d) => d.kind === 'videoinput');
    const audioInputs = devices.filter((d) => d.kind === 'audioinput');
    const audioOutputs = devices.filter((d) => d.kind === 'audiooutput');

    return {
      selectedCamera: videoInputs[0]?.deviceId ?? '',
      selectedMicrophone: audioInputs[0]?.deviceId ?? '',
      selectedAudioOutput: audioOutputs[0]?.deviceId ?? '',
    };
  } catch {
    return { ...FALLBACK };
  }
}

const JITSI_LS_KEY = 'jitsiLocalStorage';
const JITSI_BASE_SETTINGS_KEY = 'features/base/settings';

export interface JitsiBaseSettingsDevices {
  userSelectedCameraDeviceId?: string;
  userSelectedMicDeviceId?: string;
  userSelectedAudioOutputDeviceId?: string;
}

/** Normalize Jitsi device value (string or { deviceId } object) to a device ID string. */
export function normalizeDeviceId(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  const obj = value as { deviceId?: string } | null | undefined;
  return typeof obj?.deviceId === 'string' ? obj.deviceId : '';
}

/** Normalize device label for consistent comparison (trim, collapse spaces, lower case). */
export function normalizeDeviceLabel(label?: string | null): string {
  return (label ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
}

export interface ResolveDeviceByLabelResult {
  deviceId: string;
}

/**
 * Resolve current device from Jitsi API to a device ID by matching normalized label
 * against enumerated devices. Returns the new deviceId if it differs from currentStoredId.
 */
export function resolveDeviceByLabel(
  devices: MediaDeviceInfo[],
  currentDevice: unknown,
  kind: MediaDeviceInfo['kind'],
  currentStoredId: string | null,
): ResolveDeviceByLabelResult | null {
  const label = (currentDevice as { label?: string } | null)?.label;
  const normalizedLabel = normalizeDeviceLabel(label);
  if (!normalizedLabel) {
    return null;
  }
  const match = devices.find(
    (d) => d.kind === kind && normalizeDeviceLabel(d.label) === normalizedLabel,
  );
  if (!match || match.deviceId === currentStoredId) {
    return null;
  }
  return { deviceId: match.deviceId };
}

let jitsiSessionActive = false;

/**
 * While a Jitsi iframe is live it owns jitsiLocalStorage (we start it with
 * `useHostPageLocalStorage`), and it persists its whole redux blob under a
 * single key. Our read-modify-write of that key can therefore drop changes
 * Jitsi made in between, and re-asserting a deviceId mid-session fights the
 * selection Jitsi is currently negotiating. Device choices still persist to
 * sangoma-meet-settings and DEVICE_LS_KEYS, which the next join reads.
 */
export function setJitsiSessionActive(active: boolean): void {
  if (jitsiSessionActive !== active) {
    console.info(
      `[DeviceUtils] jitsi session ${active ? 'active' : 'inactive'}; ` +
        `${active ? 'gating' : 'allowing'} jitsiLocalStorage device writes`,
    );
  }
  jitsiSessionActive = active;
}

const errorMessage = (e: unknown): string => (e instanceof Error ? e.message : String(e));

export function updateJitsiLocalStorageDevices(devices: {
  selectedCamera?: string;
  selectedMicrophone?: string;
  selectedAudioOutput?: string;
}): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  if (jitsiSessionActive) {
    console.info(
      '[DeviceUtils] skipping jitsiLocalStorage device id update: jitsi session is active',
    );
    return;
  }

  try {
    const raw = localStorage.getItem(JITSI_LS_KEY);
    const jitsiData: Record<string, string> = raw ? JSON.parse(raw) : {};

    let baseSettings: Record<string, unknown> = {};
    if (jitsiData[JITSI_BASE_SETTINGS_KEY]) {
      try {
        baseSettings = JSON.parse(jitsiData[JITSI_BASE_SETTINGS_KEY]);
      } catch {
        baseSettings = {};
      }
    }

    if (devices.selectedCamera !== undefined) {
      baseSettings.userSelectedCameraDeviceId = devices.selectedCamera || undefined;
    }
    if (devices.selectedMicrophone !== undefined) {
      baseSettings.userSelectedMicDeviceId = devices.selectedMicrophone || undefined;
    }
    if (devices.selectedAudioOutput !== undefined) {
      baseSettings.userSelectedAudioOutputDeviceId = devices.selectedAudioOutput || undefined;
    }

    jitsiData[JITSI_BASE_SETTINGS_KEY] = JSON.stringify(baseSettings);
    localStorage.setItem(JITSI_LS_KEY, JSON.stringify(jitsiData));
  } catch {
    // ignore
  }
}

/**
 * Seed Jitsi's own start-muted flags. Prejoin mute buttons hydrate from
 * `features/base/settings` when `useHostPageLocalStorage` is on, so writing
 * only `configOverwrite` is not enough — a previous meeting that ended
 * unmuted leaves `startWithAudioMuted: false` in this blob and wins.
 */
export function updateJitsiLocalStorageStartMuted(flags: {
  startWithAudioMuted: boolean;
  startWithVideoMuted: boolean;
}): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  if (jitsiSessionActive) {
    console.info(
      '[DeviceUtils] skipping jitsiLocalStorage start-muted update: jitsi session is active',
    );
    return;
  }

  try {
    const raw = localStorage.getItem(JITSI_LS_KEY);
    const jitsiData: Record<string, string> = raw ? JSON.parse(raw) : {};

    let baseSettings: Record<string, unknown> = {};
    if (jitsiData[JITSI_BASE_SETTINGS_KEY]) {
      try {
        baseSettings = JSON.parse(jitsiData[JITSI_BASE_SETTINGS_KEY]);
      } catch {
        baseSettings = {};
      }
    }

    baseSettings.startWithAudioMuted = flags.startWithAudioMuted;
    baseSettings.startWithVideoMuted = flags.startWithVideoMuted;

    jitsiData[JITSI_BASE_SETTINGS_KEY] = JSON.stringify(baseSettings);
    localStorage.setItem(JITSI_LS_KEY, JSON.stringify(jitsiData));
  } catch {
    // ignore
  }
}

/**
 * Best-effort: looks up the current label for each given deviceId and persists
 * it into jitsiLocalStorage alongside the ID. This label is the recovery key
 * `reconcileStoredDeviceIds` uses when a deviceId goes stale (e.g. an external
 * device replugged with a new ID) — so every save path needs to keep it fresh,
 * not just the ones that already had enumerated devices on hand.
 * Fire-and-forget: enumerateDevices is async, so this must not block callers.
 */
export async function updateJitsiLocalStorageDeviceLabels(devices: {
  selectedCamera?: string;
  selectedMicrophone?: string;
  selectedAudioOutput?: string;
}): Promise<void> {
  if (
    typeof navigator === 'undefined' ||
    !navigator.mediaDevices?.enumerateDevices ||
    typeof localStorage === 'undefined'
  ) {
    return;
  }
  if (jitsiSessionActive) {
    console.info(
      '[DeviceUtils] skipping jitsiLocalStorage device label update: jitsi session is active',
    );
    return;
  }

  try {
    const enumerated = await navigator.mediaDevices.enumerateDevices();
    const findLabel = (
      id: string | undefined,
      kind: MediaDeviceInfo['kind'],
    ): string | undefined =>
      id
        ? enumerated.find((d) => d.kind === kind && d.deviceId === id)?.label || undefined
        : undefined;

    const cameraLabel = findLabel(devices.selectedCamera, 'videoinput');
    const micLabel = findLabel(devices.selectedMicrophone, 'audioinput');
    const audioOutputLabel = findLabel(devices.selectedAudioOutput, 'audiooutput');

    if (!(cameraLabel || micLabel || audioOutputLabel)) {
      return;
    }

    const raw = localStorage.getItem(JITSI_LS_KEY);
    const jitsiData: Record<string, string> = raw ? JSON.parse(raw) : {};

    let baseSettings: Record<string, unknown> = {};
    if (jitsiData[JITSI_BASE_SETTINGS_KEY]) {
      try {
        baseSettings = JSON.parse(jitsiData[JITSI_BASE_SETTINGS_KEY]);
      } catch {
        baseSettings = {};
      }
    }

    if (cameraLabel) {
      baseSettings.userSelectedCameraDeviceLabel = cameraLabel;
    }
    if (micLabel) {
      baseSettings.userSelectedMicDeviceLabel = micLabel;
    }
    if (audioOutputLabel) {
      baseSettings.userSelectedAudioOutputDeviceLabel = audioOutputLabel;
    }

    jitsiData[JITSI_BASE_SETTINGS_KEY] = JSON.stringify(baseSettings);
    localStorage.setItem(JITSI_LS_KEY, JSON.stringify(jitsiData));
  } catch {
    // best-effort only; a missing label just means recovery falls back later
  }
}

/** Read the canonical device selections from sangoma-meet-settings, falling back to DEVICE_LS_KEYS. */
export function getPersistedDeviceSelections(): CurrentBrowserDevices {
  if (typeof localStorage === 'undefined') {
    return { ...FALLBACK };
  }

  try {
    const stored = localStorage.getItem(SANGOMA_SETTINGS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as { devices?: Partial<CurrentBrowserDevices> };
      if (parsed.devices) {
        return {
          selectedCamera: parsed.devices.selectedCamera ?? '',
          selectedMicrophone: parsed.devices.selectedMicrophone ?? '',
          selectedAudioOutput: parsed.devices.selectedAudioOutput ?? '',
        };
      }
    }
  } catch (e) {
    console.info(`[DeviceUtils] skipping stored device settings: ${errorMessage(e)}`);
  }

  return {
    selectedCamera: localStorage.getItem(DEVICE_LS_KEYS.camera) ?? '',
    selectedMicrophone: localStorage.getItem(DEVICE_LS_KEYS.microphone) ?? '',
    selectedAudioOutput: localStorage.getItem(DEVICE_LS_KEYS.audioOutput) ?? '',
  };
}

/** Mute toggles from sangoma-meet-settings; Jitsi defaults both to false. */
export function getPersistedStartMuted(): {
  startWithAudioMuted: boolean;
  startWithVideoMuted: boolean;
} {
  if (typeof localStorage === 'undefined') {
    return { startWithAudioMuted: false, startWithVideoMuted: false };
  }

  try {
    const stored = localStorage.getItem(SANGOMA_SETTINGS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as {
        devices?: { startWithAudioMuted?: boolean; startWithVideoMuted?: boolean };
      };
      if (parsed.devices) {
        return {
          startWithAudioMuted: !!parsed.devices.startWithAudioMuted,
          startWithVideoMuted: !!parsed.devices.startWithVideoMuted,
        };
      }
    }
  } catch (e) {
    console.info(`[DeviceUtils] skipping stored start-muted settings: ${errorMessage(e)}`);
  }

  return { startWithAudioMuted: false, startWithVideoMuted: false };
}

/** Keep the flat DEVICE_LS_KEYS in sync with the canonical sangoma-meet-settings values. */
export function syncDeviceLsKeys(devices: CurrentBrowserDevices): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  localStorage.setItem(DEVICE_LS_KEYS.camera, devices.selectedCamera ?? '');
  localStorage.setItem(DEVICE_LS_KEYS.microphone, devices.selectedMicrophone ?? '');
  localStorage.setItem(DEVICE_LS_KEYS.audioOutput, devices.selectedAudioOutput ?? '');
}

/**
 * Write device *labels* directly into jitsiLocalStorage (the inverse of
 * `getStoredJitsiDeviceLabels`). Unlike `updateJitsiLocalStorageDeviceLabels`,
 * this does not resolve labels from deviceIds by enumeration — it stores the
 * provided labels verbatim, which is what the MF device bridge needs when
 * seeding a selection that originated on a different origin.
 */
export function setStoredJitsiDeviceLabels(labels: {
  camera?: string;
  microphone?: string;
  audioOutput?: string;
}): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  if (!(labels.camera || labels.microphone || labels.audioOutput)) {
    console.info('[DeviceUtils] no device settings to store');
    return;
  }
  try {
    const raw = localStorage.getItem(JITSI_LS_KEY);
    const jitsiData: Record<string, string> = raw ? JSON.parse(raw) : {};

    let baseSettings: Record<string, unknown> = {};
    if (jitsiData[JITSI_BASE_SETTINGS_KEY]) {
      try {
        baseSettings = JSON.parse(jitsiData[JITSI_BASE_SETTINGS_KEY]);
      } catch {
        baseSettings = {};
      }
    }

    if (labels.camera) {
      baseSettings.userSelectedCameraDeviceLabel = labels.camera;
    }
    if (labels.microphone) {
      baseSettings.userSelectedMicDeviceLabel = labels.microphone;
    }
    if (labels.audioOutput) {
      baseSettings.userSelectedAudioOutputDeviceLabel = labels.audioOutput;
    }

    jitsiData[JITSI_BASE_SETTINGS_KEY] = JSON.stringify(baseSettings);
    localStorage.setItem(JITSI_LS_KEY, JSON.stringify(jitsiData));
  } catch (e) {
    console.info(`[DeviceUtils] not storing device settings: ${errorMessage(e)}`);
  }
}

export function getStoredJitsiDeviceLabels(): {
  camera?: string;
  microphone?: string;
  audioOutput?: string;
} {
  if (typeof localStorage === 'undefined') {
    return {};
  }
  try {
    const raw = localStorage.getItem(JITSI_LS_KEY);
    if (!raw) {
      return {};
    }
    const jitsiData = JSON.parse(raw);
    const baseSettingsRaw = jitsiData[JITSI_BASE_SETTINGS_KEY];
    if (!baseSettingsRaw) {
      return {};
    }
    const baseSettings = JSON.parse(baseSettingsRaw);
    return {
      camera: baseSettings.userSelectedCameraDeviceLabel,
      microphone: baseSettings.userSelectedMicDeviceLabel,
      audioOutput: baseSettings.userSelectedAudioOutputDeviceLabel,
    };
  } catch {
    return {};
  }
}

export function findDeviceByStoredLabel(
  devices: MediaDeviceInfo[],
  kind: MediaDeviceInfo['kind'],
  label: string | undefined,
): MediaDeviceInfo | null {
  const normalized = normalizeDeviceLabel(label);
  if (!normalized) {
    return null;
  }
  const candidates = devices.filter((d) => d.kind === kind);
  const exact = candidates.find((d) => normalizeDeviceLabel(d.label) === normalized);
  if (exact) {
    return exact;
  }
  // Substring matching absorbs the decoration browsers add to the same physical
  // device ("Default - Yeti" vs "Yeti"). Unlabelled candidates are skipped
  // because every string contains the empty one: Chrome reports real deviceIds
  // with blank labels for kinds the user has not granted permission to, and
  // those would otherwise match any saved label and select the wrong device.
  return (
    candidates.find((d) => {
      const candidate = normalizeDeviceLabel(d.label);
      return !!candidate && (candidate.includes(normalized) || normalized.includes(candidate));
    }) ?? null
  );
}

export interface DeviceReconciliationResult {
  selectedCamera?: string;
  selectedMicrophone?: string;
  selectedAudioOutput?: string;
}

/**
 * Validates the persisted camera/mic/audio-output device IDs against the
 * devices currently available in the browser.
 *
 * DeviceIds for external/USB devices are not guaranteed stable across
 * unplug/replug, sleep/wake, or reboot the way built-in device IDs are
 * (SM-1151) — so a stored ID can silently go stale. When that happens, this
 * attempts to recover the correct device by matching its last-known label
 * (persisted via `updateJitsiLocalStorageDeviceLabels`).
 *
 * Returns only the corrections that are needed (deviceIds that changed) so
 * the caller can decide how to persist them; an empty object means either
 * nothing was stale or a stale ID couldn't be recovered.
 */
export async function reconcileStoredDeviceIds(): Promise<DeviceReconciliationResult> {
  const corrections: DeviceReconciliationResult = {};

  if (
    typeof navigator === 'undefined' ||
    !navigator.mediaDevices?.enumerateDevices ||
    typeof localStorage === 'undefined'
  ) {
    return corrections;
  }

  let devices: MediaDeviceInfo[];
  try {
    devices = await navigator.mediaDevices.enumerateDevices();
  } catch {
    return corrections;
  }

  const labels = getStoredJitsiDeviceLabels();

  const checks: Array<{
    lsKey: string;
    kind: MediaDeviceInfo['kind'];
    resultKey: keyof DeviceReconciliationResult;
    label: string | undefined;
  }> = [
    {
      lsKey: DEVICE_LS_KEYS.camera,
      kind: 'videoinput',
      resultKey: 'selectedCamera',
      label: labels.camera,
    },
    {
      lsKey: DEVICE_LS_KEYS.microphone,
      kind: 'audioinput',
      resultKey: 'selectedMicrophone',
      label: labels.microphone,
    },
    {
      lsKey: DEVICE_LS_KEYS.audioOutput,
      kind: 'audiooutput',
      resultKey: 'selectedAudioOutput',
      label: labels.audioOutput,
    },
  ];

  for (const { lsKey, kind, resultKey, label } of checks) {
    const storedId = localStorage.getItem(lsKey);
    if (!storedId) {
      continue;
    }
    const stillExists = devices.some((d) => d.kind === kind && d.deviceId === storedId);
    if (stillExists) {
      continue;
    }
    const recovered = findDeviceByStoredLabel(devices, kind, label);
    if (recovered && recovered.deviceId !== storedId) {
      corrections[resultKey] = recovered.deviceId;
    }
  }

  return corrections;
}

export interface PrepareDevicesForMeetingJoinResult {
  devices: CurrentBrowserDevices;
  corrections: DeviceReconciliationResult;
}

/**
 * Prepare persisted device selections for a meeting join.
 *
 * Sangoma settings are the source of truth. DEVICE_LS_KEYS and jitsiLocalStorage
 * can drift (e.g. Jitsi wrote a stale built-in mic id during a prior session while
 * the user saved an external mic in Settings). Jitsi reads jitsiLocalStorage on
 * startup with useHostPageLocalStorage, so we reconcile IDs and push the final
 * selection into both stores before building the join config.
 */
export async function prepareDevicesForMeetingJoin(): Promise<PrepareDevicesForMeetingJoinResult> {
  let devices = getPersistedDeviceSelections();
  syncDeviceLsKeys(devices);

  const corrections = await reconcileStoredDeviceIds();
  if (Object.keys(corrections).length > 0) {
    devices = { ...devices, ...corrections };
    syncDeviceLsKeys(devices);
  }

  updateJitsiLocalStorageDevices(devices);
  await updateJitsiLocalStorageDeviceLabels(devices);
  updateJitsiLocalStorageStartMuted(getPersistedStartMuted());

  return { devices, corrections };
}

interface JitsiAvailableDevices {
  audioInput?: MediaDeviceInfo[];
  audioOutput?: MediaDeviceInfo[];
  videoInput?: MediaDeviceInfo[];
}

/** What Jitsi reports as backing the currently active track for each slot. */
interface JitsiCurrentDevices {
  audioInput?: { label?: string } | null;
  audioOutput?: { label?: string } | null;
  videoInput?: { label?: string } | null;
}

interface JitsiDeviceSetterApi {
  setAudioInputDevice?: (label: string, deviceId?: string) => Promise<void> | void;
  setAudioOutputDevice?: (label: string, deviceId?: string) => Promise<void> | void;
  setVideoInputDevice?: (label: string, deviceId?: string) => Promise<void> | void;
  getAvailableDevices?: () => Promise<JitsiAvailableDevices>;
  getCurrentDevices?: () => Promise<JitsiCurrentDevices>;
}

/**
 * Resolve the human-readable label for each persisted device selection.
 *
 * Prefers the labels persisted alongside the IDs in jitsiLocalStorage
 * (`updateJitsiLocalStorageDeviceLabels`); falls back to enumerating the
 * host-page devices and mapping the stored deviceId to its current label.
 */
export async function resolveSelectionLabels(devices: CurrentBrowserDevices): Promise<{
  camera?: string;
  microphone?: string;
  audioOutput?: string;
}> {
  const stored = getStoredJitsiDeviceLabels();
  const result = {
    camera: stored.camera,
    microphone: stored.microphone,
    audioOutput: stored.audioOutput,
  };

  const needsHostLookup =
    (devices.selectedCamera && !result.camera) ||
    (devices.selectedMicrophone && !result.microphone) ||
    (devices.selectedAudioOutput && !result.audioOutput);

  if (
    needsHostLookup &&
    typeof navigator !== 'undefined' &&
    navigator.mediaDevices?.enumerateDevices
  ) {
    console.debug(
      `[DeviceUtils] performing host devices lookup, camera=${devices.selectedCamera} ` +
        `mic=${devices.selectedMicrophone} output=${devices.selectedAudioOutput}`,
    );
    try {
      const enumerated = await navigator.mediaDevices.enumerateDevices();
      const labelFor = (id: string, kind: MediaDeviceInfo['kind']): string | undefined =>
        id
          ? enumerated.find((d) => d.kind === kind && d.deviceId === id)?.label || undefined
          : undefined;
      result.camera ??= labelFor(devices.selectedCamera, 'videoinput');
      result.microphone ??= labelFor(devices.selectedMicrophone, 'audioinput');
      result.audioOutput ??= labelFor(devices.selectedAudioOutput, 'audiooutput');
      console.debug('[DeviceUtils] host devices lookup resolved', {
        camera: result.camera,
        microphone: result.microphone,
        audioOutput: result.audioOutput,
      });
    } catch (e) {
      console.info(`[DeviceUtils] skipping host devices lookup: ${errorMessage(e)}`);
    }
  }

  return result;
}

/** The three device slots the Jitsi apply can satisfy, tracked independently. */
export type DeviceApplyKind = 'camera' | 'microphone' | 'audioOutput';

/**
 * Apply the persisted device selection through the Jitsi External API so the
 * in-meeting device menu reflects the saved microphone/speaker/camera.
 *
 * IMPORTANT (SM-1151): `MediaDeviceInfo.deviceId` is salted per origin, and the
 * Jitsi conference runs in a cross-origin iframe. The deviceId we persisted from
 * the host page therefore never matches the ids Jitsi enumerates inside the
 * iframe, and passing a non-matching id prevents Jitsi from resolving the device
 * at all. Device *labels*, however, are stable across origins, so we resolve the
 * saved selection to a label and set by label only (confirmed by testing:
 * `setAudioInputDevice(label)` selects the device; passing the host-scoped id
 * does not).
 *
 * Applying is once-per-slot: pass the slots already satisfied in `alreadyApplied`
 * and this skips them, returning the slots it newly applied so the caller can
 * accumulate them. That lets the caller drive this from Jitsi's
 * `deviceListChanged` event without ever overriding a device the user picked
 * themselves mid-meeting. A slot whose device is absent from Jitsi's list is
 * deliberately left unapplied so a later event (e.g. a USB mic plugged in) can
 * still satisfy it.
 */
export async function applyPersistedDevicesToJitsiApi(
  api: JitsiDeviceSetterApi,
  devices: CurrentBrowserDevices = getPersistedDeviceSelections(),
  alreadyApplied: ReadonlySet<DeviceApplyKind> = new Set(),
): Promise<Set<DeviceApplyKind>> {
  const applied = new Set<DeviceApplyKind>();
  if (!(devices.selectedCamera || devices.selectedMicrophone || devices.selectedAudioOutput)) {
    return applied;
  }

  const labels = await resolveSelectionLabels(devices);
  devLog('[DeviceApply] resolved labels from saved selection', { devices, labels });

  let jitsiDevices: JitsiAvailableDevices | undefined;
  if (typeof api.getAvailableDevices === 'function') {
    try {
      jitsiDevices = await api.getAvailableDevices();
    } catch (e) {
      console.warn(`[DeviceUtils] getAvailableDevices() failed: ${errorMessage(e)}`);
    }
  }

  // What Jitsi is already using, so we can leave a correct slot alone. Failure
  // is fine: an unknown current device just means we fall through and apply.
  let currentDevices: JitsiCurrentDevices | undefined;
  if (typeof api.getCurrentDevices === 'function') {
    try {
      currentDevices = await api.getCurrentDevices();
    } catch (e) {
      console.info(`[DeviceUtils] getCurrentDevices() failed: ${errorMessage(e)}`);
    }
  }

  // The external API's setters call `this._transport` internally, so they must be
  // invoked with `api` as the receiver. Passing a bare method reference makes
  // every apply throw `Cannot read properties of undefined (reading '_transport')`.
  const bind = (
    setter: ((label: string, id?: string) => Promise<void> | void) | undefined,
  ): ((label: string) => Promise<void> | void) | undefined =>
    typeof setter === 'function' ? (label: string) => setter.call(api, label) : undefined;

  const applyDevice = async (
    slot: DeviceApplyKind,
    label: string | undefined,
    kind: MediaDeviceInfo['kind'],
    available: MediaDeviceInfo[] | undefined,
    currentLabel: string | undefined,
    setter: ((label: string) => Promise<void> | void) | undefined,
  ): Promise<void> => {
    if (alreadyApplied.has(slot) || !(label && setter)) {
      return;
    }
    // Jitsi is already on the saved device. Setting it again would force a
    // pointless track replacement, and doing that while the conference is still
    // coming up can wedge it. Count the slot as satisfied so later device-list
    // changes don't retry it. Compared exactly: a fuzzy match here could skip a
    // slot that genuinely needs applying.
    if (currentLabel && normalizeDeviceLabel(currentLabel) === normalizeDeviceLabel(label)) {
      devLog('[DeviceApply]', kind, 'already active, leaving it alone:', label);
      applied.add(slot);
      return;
    }
    // When Jitsi's list is available and the device isn't in it, there is
    // nothing to select yet — stay unapplied so a later list change retries.
    if (available && !findDeviceByStoredLabel(available, kind, label)) {
      devLog('[DeviceApply] saved', kind, 'not present in Jitsi list yet', label);
      return;
    }
    // Set by LABEL ONLY (see function docstring): the origin-salted deviceId can
    // never match inside Jitsi's iframe, and passing it blocks the label match.
    try {
      devLog('[DeviceApply] setting', kind, 'by label', label);
      await setter(label);
      applied.add(slot);
    } catch (e) {
      devLog('[DeviceApply] setter threw for', kind, e);
    }
  };

  await applyDevice(
    'microphone',
    labels.microphone,
    'audioinput',
    jitsiDevices?.audioInput,
    currentDevices?.audioInput?.label,
    bind(api.setAudioInputDevice),
  );
  await applyDevice(
    'audioOutput',
    labels.audioOutput,
    'audiooutput',
    jitsiDevices?.audioOutput,
    currentDevices?.audioOutput?.label,
    bind(api.setAudioOutputDevice),
  );
  await applyDevice(
    'camera',
    labels.camera,
    'videoinput',
    jitsiDevices?.videoInput,
    currentDevices?.videoInput?.label,
    bind(api.setVideoInputDevice),
  );

  return applied;
}
